import {
  Ard,
  DebitOrCredit,
  ProductType,
  SubscriptionProductLine,
  TemplateType,
  UslLine,
} from "@amzn/fuse-accounting-central-datastore-client";
import { UslLineType } from "@amzn/fuse-flare-common";
import {
  GlLineDescriptionSuffixHelper,
  isReceivableAccount,
  isDeferredRevenueAccount,
} from "./constants";

// The interfaces FlareMappingData, FlareNode, and RawFlareTree are now imported from the common package

export const FLARE_PATH_TO_ARD_MAPPING: Partial<
  Record<
    string,
    | keyof Ard
    | ((ard: Ard, uslLine: UslLine) => SubscriptionProductLine | undefined)
  >
> = {
  business_country_code: "businessCountryCode",
  buying_customer_country_code: "buyingCustomerCountryCode",
  fuse_provider_name: "product",
  fuse_subscription_term: "term",
  subscription_product_line: getSubscriptionProductLine,
  deferral_element: getSubscriptionProductLine,
};

export const getUslLineTypeFromUsl = (
  uslLine: UslLine,
): UslLineType | undefined => {
  const isReceivable = isReceivableAccount(uslLine.accountNumber);
  const isDeferredRevenue = isDeferredRevenueAccount(uslLine.accountNumber);

  // Check RECEIVABLE
  if (isReceivable) {
    return UslLineType.RECEIVABLE;
  }

  // Check DEFERRED_REVENUE
  if (isDeferredRevenue) {
    return UslLineType.DEFERRED_REVENUE;
  }

  // Check REVENUE
  if (
    (GlLineDescriptionSuffixHelper.isAmortization(
      uslLine.glLineDescriptionSuffix,
    ) &&
      uslLine.debitOrCredit === DebitOrCredit.CREDIT) ||
    (GlLineDescriptionSuffixHelper.isCancelationTrueUp(
      uslLine.glLineDescriptionSuffix,
    ) &&
      uslLine.debitOrCredit === DebitOrCredit.CREDIT)
  ) {
    return UslLineType.REVENUE;
  }

  // Check INTERCOMPANY_FROM
  if (
    ((GlLineDescriptionSuffixHelper.isSale(uslLine.glLineDescriptionSuffix) &&
      uslLine.debitOrCredit === DebitOrCredit.CREDIT) ||
      (GlLineDescriptionSuffixHelper.isCancelation(
        uslLine.glLineDescriptionSuffix,
      ) &&
        uslLine.debitOrCredit === DebitOrCredit.DEBIT)) &&
    !isReceivable &&
    !isDeferredRevenue
  ) {
    return UslLineType.INTERCOMPANY_FROM;
  }

  // Check INTERCOMPANY_TO
  if (
    ((GlLineDescriptionSuffixHelper.isSale(uslLine.glLineDescriptionSuffix) &&
      uslLine.debitOrCredit === DebitOrCredit.DEBIT) ||
      (GlLineDescriptionSuffixHelper.isCancelation(
        uslLine.glLineDescriptionSuffix,
      ) &&
        uslLine.debitOrCredit === DebitOrCredit.CREDIT)) &&
    !isReceivable &&
    !isDeferredRevenue
  ) {
    return UslLineType.INTERCOMPANY_TO;
  }

  return undefined;
};

/**
 * Determines the subscription product line based on ARD template and product type
 * @param ard - The ARD object containing template and product information
 * @param uslLine - The USL line object that may contain subscription product line
 * @returns The appropriate SubscriptionProductLine or undefined if unsupported
 */
export function getSubscriptionProductLine(
  ard: Ard,
  uslLine: UslLine,
): SubscriptionProductLine | undefined {
  if (ard.template === TemplateType.STANDARD) {
    switch (ard.product) {
      case ProductType.AUDIBLE:
        return SubscriptionProductLine.AUDIBLE;
      case ProductType.AVCHANNELS:
        return SubscriptionProductLine.CHANNEL;
      case ProductType.KINDLE:
        return SubscriptionProductLine.EBOOKS;
      case ProductType.MUSIC:
        return SubscriptionProductLine.MUSIC;
      case ProductType.PRIME:
        return SubscriptionProductLine.SHIPPING;
      case ProductType.PVGO:
      case ProductType.VIDEO:
        return SubscriptionProductLine.VIDEO;
      case ProductType.PV_AD_FREE:
        return SubscriptionProductLine.PV_AD_FREE;
      default:
        // Unsupported ARD product for standard template
        return undefined;
    }
  } else {
    return uslLine.subscriptionProductLine;
  }
}
